import {
  EMPTY_ARRAY,
  EMPTY_MAP,
  EMPTY_OBJECT,
  useMarketAccountsContext,
  type ICounterparty,
  type MarketAccountTypeEnum,
} from '@talos/kyoko';
import {
  useGetCounterpartiesQuery,
  useGetMarketAccountDetailsQuery,
} from 'ava/src/providers/AppStateProvider/streamingDataSlice';
import { useMemo } from 'react';
import type { MarketAccountDetailsMessageData } from '../../containers/Admin/Counterparties/CounterpartyTypes';

/**
 * Hook that provides OTC-specific counterparties data
 * Returns OTC counterparties and deal-assignable counterparties based on market account types and agreements
 */
export function useOtcCounterpartiesQueryData(): {
  /** OTC Counterparties that have OTC Market Accounts (OTCDerivatives, OTCLending, or OTCRepo) by name */
  otcCounterpartiesByName: Map<string, ICounterparty>;
  /** OTC Counterparties that have OTC Market Accounts (OTCDerivatives, OTCLending, or OTCRepo) */
  dealAssignableCounterparties: ICounterparty[];
  /** OTC Counterparties that have OTC Market Accounts (OTCDerivatives, OTCLending, or OTCRepo) by name */
  dealAssignableCounterpartiesByName: Map<string, ICounterparty>;
} {
  // TODO - once market accounts moves to RTK Query, we should use selectors instead of useMemo's
  const { marketAccountsByCounterparty } = useMarketAccountsContext();
  const { data: counterpartiesByName } = useGetCounterpartiesQuery(EMPTY_OBJECT);
  const { data: marketAccountDetailsData } = useGetMarketAccountDetailsQuery(EMPTY_OBJECT);

  return useMemo(() => {
    if (!counterpartiesByName) {
      return {
        otcCounterpartiesByName: EMPTY_MAP,
        dealAssignableCounterparties: EMPTY_ARRAY,
        dealAssignableCounterpartiesByName: EMPTY_MAP,
      };
    }
    const allCounterparties = Array.from(counterpartiesByName.values());
    // Create a map of MarketAccount name to MarketAccountDetails for quick lookup
    const marketAccountDetailsByName = new Map<string, MarketAccountDetailsMessageData>();
    if (marketAccountDetailsData) {
      marketAccountDetailsData.forEach((details, marketAccountName) => {
        marketAccountDetailsByName.set(marketAccountName, details);
      });
    }

    // Helper function to check if a market account has a defined agreement matching its type
    // TypeScript's discriminated union guarantees Agreement is defined when AgreementType matches
    const hasDefinedAgreement = (marketAccountName: string, marketAccountType: MarketAccountTypeEnum): boolean => {
      const details = marketAccountDetailsByName.get(marketAccountName);
      if (!details || !details.Agreement) {
        return false;
      }
      // For OTCDerivatives, check for CSA agreement
      if (marketAccountType === 'OTCDerivatives') {
        return details.AgreementType === 'CSA';
      }
      // For OTCLending, check for MLA agreement
      if (marketAccountType === 'OTCLending') {
        return details.AgreementType === 'MLA';
      }
      // For OTCRepo, check for GMRA agreement
      if (marketAccountType === 'OTCRepo') {
        return details.AgreementType === 'GMRA';
      }
      return false;
    };

    const isOtcMarketAccountType = (marketAccountType: MarketAccountTypeEnum) =>
      marketAccountType === 'OTCDerivatives' || marketAccountType === 'OTCLending' || marketAccountType === 'OTCRepo';

    // OTC Counterparties are counterparties that have OTC Market Accounts
    const otcCounterparties = allCounterparties.filter(counterparty => {
      const marketAccounts = marketAccountsByCounterparty.get(counterparty.Name);
      if (!marketAccounts) {
        return false;
      }
      return marketAccounts.some(marketAccount => isOtcMarketAccountType(marketAccount.Type));
    });
    const otcCounterpartiesByName = new Map(otcCounterparties.map(counterparty => [counterparty.Name, counterparty]));

    // Deal Assignable Counterparties are counterparties that have OTC Market Accounts
    // AND those market accounts must have defined Agreements (CSA, MLA, or GMRA)
    const dealAssignableCounterparties = allCounterparties.filter(counterparty => {
      const marketAccounts = marketAccountsByCounterparty.get(counterparty.Name);
      if (!marketAccounts) {
        return false;
      }
      return marketAccounts.some(marketAccount => {
        if (!isOtcMarketAccountType(marketAccount.Type)) {
          return false;
        }
        // Check that the market account has a defined agreement
        return hasDefinedAgreement(marketAccount.Name, marketAccount.Type);
      });
    });

    return {
      otcCounterpartiesByName,
      dealAssignableCounterparties,
      dealAssignableCounterpartiesByName: new Map(
        dealAssignableCounterparties.map(counterparty => [counterparty.Name, counterparty])
      ),
    };
  }, [marketAccountDetailsData, counterpartiesByName, marketAccountsByCounterparty]);
}
